import { useEffect, useState } from 'react';

import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  DropdownV2,
  EquipUITheme,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { PatientActionEvents } from '~/analytics/patientActionEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useDatadog } from '~/lib/hooks/useDatadog';
import {
  GENERATE_PATIENT_REPORT_CANCEL_CTA,
  GENERATE_PATIENT_REPORT_CREATE_CTA,
  GENERATE_PATIENT_REPORT_ISSUES_DESCRIPTION,
  GENERATE_PATIENT_REPORT_ISSUES_TITLE,
  GENERATE_PATIENT_REPORT_MODAL_DESCRIPTION,
  GENERATE_PATIENT_REPORT_MODAL_TITLE,
  GENERATE_PATIENT_REPORT_PROCEED_CTA,
  GENERATE_PATIENT_REPORT_REPORT_TYPE_LABEL,
} from '~/lib/constants/patientReport.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import {
  PatientReportType,
  PatientReportTypeLabels,
} from '~/lib/types/patient/PatientReportType';
import { Path } from '~/routes/constants';
import {
  useCreateTreatmentSummaryMutation,
  useLazyValidateTreatmentSummaryQuery,
} from '~/store/services/clinical-data/treatmentSummary';

const { paragraphBold } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'generate-patient-report-modal';

interface ReportTypeOption {
  label: string;
  value: PatientReportType;
}

const REPORT_TYPE_OPTIONS: ReportTypeOption[] = Object.values(
  PatientReportType,
).map((value) => ({
  label: PatientReportTypeLabels[value],
  value,
}));

const isPatientReportType = (value: string): value is PatientReportType =>
  Object.values(PatientReportType).some((v) => v === value);

interface GeneratePatientReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientExternalId: string;
  patientMrn: string | undefined;
}

const GeneratePatientReportModal = ({
  isOpen,
  onClose,
  patientExternalId,
  patientMrn,
}: GeneratePatientReportModalProps) => {
  const { trackEvent } = useAnalytics();
  const { logError } = useDatadog();
  const { createToast } = useToastMessage();
  const history = useHistory();

  const [reportType, setReportType] = useState<PatientReportType | null>(null);
  const [
    triggerValidate,
    {
      data: validation,
      isFetching: isValidating,
      reset: resetValidate,
    },
  ] = useLazyValidateTreatmentSummaryQuery();
  const [createTreatmentSummary, { isLoading: isCreatingReport }] =
    useCreateTreatmentSummaryMutation();

  const dataIssues = validation?.dataIssues ?? [];
  const issueCodes = validation?.issueCodes ?? [];
  const showIssuesView =
    !!reportType && !isValidating && dataIssues.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setReportType(null);
      resetValidate();
    }
  }, [isOpen, resetValidate]);

  const handleClose = () => {
    trackEvent(PatientActionEvents.generatePatientReportModalDismissed, {
      ReportType: reportType ?? 'Not selected',
      AtIssuesView: showIssuesView,
      WithIssues: issueCodes.length > 0,
      IssueCount: issueCodes.length,
      IssueCodes: issueCodes,
    });
    onClose();
  };

  const handleReportTypeChange = async (value: string) => {
    if (!isPatientReportType(value) || value === reportType) return;
    setReportType(value);
    if (!patientMrn) {
      createToast({
        id: 'generate-patient-report-missing-mrn',
        position: 'top',
        text: 'Patient MRN is missing. Cannot generate report.',
        variant: 'critical',
      });
      return;
    }
    try {
      const result = await triggerValidate({ patientMrn }).unwrap();
      const codes = result.issueCodes;
      trackEvent(PatientActionEvents.generatePatientReportTypeSelected, {
        ReportType: value,
        WithIssues: codes.length > 0,
        IssueCount: codes.length,
        IssueCodes: codes,
      });
      codes.forEach((code) => {
        trackEvent(PatientActionEvents.generatePatientReportWarningSurfaced, {
          ReportType: value,
          Code: code,
        });
      });
    } catch (error) {
      resetValidate();
      logError(error, {
        action: 'validateTreatmentSummary',
        patientId: patientExternalId,
      });
      createToast({
        id: 'validate-patient-report-error',
        position: 'top',
        text: 'Could not check the patient chart for issues. Please try again.',
        variant: 'critical',
      });
    }
  };

  const handleCreateReport = async () => {
    if (!reportType) return;
    if (!patientMrn) {
      createToast({
        id: 'generate-patient-report-missing-mrn',
        text: 'Patient MRN is missing. Cannot generate report.',
        variant: 'critical',
      });
      return;
    }
    const withIssues = issueCodes.length > 0;
    trackEvent(PatientActionEvents.generatePatientReportCreateClicked, {
      ReportType: reportType,
      WithIssues: withIssues,
      IssueCount: issueCodes.length,
      IssueCodes: issueCodes,
    });
    const startedAt = performance.now();
    try {
      const response = await createTreatmentSummary({
        patientExternalId,
        patientMrn,
      }).unwrap();
      trackEvent(PatientActionEvents.generatePatientReportCreateSucceeded, {
        ReportType: reportType,
        DocumentExternalId: response.documentExternalId,
        DurationMs: Math.round(performance.now() - startedAt),
        WithIssues: withIssues,
      });
      createToast({
        id: 'generate-patient-report-success',
        position: 'top',
        text: 'Report is being generated and will appear in the Documents tab.',
        variant: 'success',
      });
      onClose();
      history.push(
        `${Path.MAUD_PATIENTS}/${patientExternalId}/${
          PatientTabsCollection[PatientTabs.DOCUMENTS].subPath
        }`,
      );
    } catch (error) {
      const httpStatus = (error as { status?: number | string } | null)?.status;
      trackEvent(PatientActionEvents.generatePatientReportCreateFailed, {
        ReportType: reportType,
        HttpStatus: typeof httpStatus === 'number' ? httpStatus : undefined,
        ErrorMessage: error instanceof Error ? error.message : String(error),
        DurationMs: Math.round(performance.now() - startedAt),
      });
      logError(error, {
        action: 'createTreatmentSummary',
        patientId: patientExternalId,
      });
      createToast({
        id: 'generate-patient-report-error',
        position: 'top',
        text: 'Failed to generate the patient report. Please try again.',
        variant: 'critical',
      });
    }
  };

  const isCreateDisabled = !reportType || isValidating || isCreatingReport;

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      title={
        showIssuesView
          ? GENERATE_PATIENT_REPORT_ISSUES_TITLE
          : GENERATE_PATIENT_REPORT_MODAL_TITLE
      }
    >
      <ChakraFlex flexDirection="column" gap="16px">
        <Text variant="body">
          {showIssuesView
            ? GENERATE_PATIENT_REPORT_ISSUES_DESCRIPTION
            : GENERATE_PATIENT_REPORT_MODAL_DESCRIPTION}
        </Text>
        {!showIssuesView && (
          <ChakraBox width="100%">
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__report-type-group`}
              label={GENERATE_PATIENT_REPORT_REPORT_TYPE_LABEL}
            >
              <DropdownV2
                ariaLabel={GENERATE_PATIENT_REPORT_REPORT_TYPE_LABEL}
                id={`${TOP_LEVEL_ID}__report-type`}
                menuOptions={REPORT_TYPE_OPTIONS}
                onOptionClick={handleReportTypeChange}
                value={reportType}
              />
            </InputGroupV2>
          </ChakraBox>
        )}
        {showIssuesView && (
          <ChakraFlex flexDirection="column" gap="8px">
            {dataIssues.map((issue) => (
              <ActionBar
                barIcon={FAIconName.exclamationTriangleRegular}
                id={`${TOP_LEVEL_ID}__issue__${issue.code.toLowerCase()}`}
                key={issue.code}
                text={
                  <Text variant="body">
                    <ChakraText as="span" {...paragraphBold}>
                      {`${issue.label}: `}
                    </ChakraText>
                    {issue.detail}
                  </Text>
                }
                variant="warning"
              />
            ))}
          </ChakraFlex>
        )}
        <ChakraFlex justifyContent="space-between">
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel`}
            isDisabled={isCreatingReport}
            leftIcon={FAIconName.XmarkRegular}
            onClick={handleClose}
            variant="secondary"
          >
            {GENERATE_PATIENT_REPORT_CANCEL_CTA}
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__create-report`}
            isDisabled={isCreateDisabled}
            isLoading={isValidating || isCreatingReport}
            onClick={handleCreateReport}
          >
            {showIssuesView
              ? GENERATE_PATIENT_REPORT_PROCEED_CTA
              : GENERATE_PATIENT_REPORT_CREATE_CTA}
          </FireflyButton>
        </ChakraFlex>
      </ChakraFlex>
    </ModalV2>
  );
};

export default GeneratePatientReportModal;
